import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Pencil, Plus } from 'lucide-react';
import { ApiError } from '@/api/client';
import {
  getHardwareCostProjects, setHardwareCostProject,
  type HardwareCostProject,
} from '@/api/hardwareCostProjects';
import { getProjectOptions } from '@/api/projectOptions';

/**
 * What a hardware purchase is charged to, per financial year (085, §21).
 *
 * ⛔ THIS USED TO BE AN APP SETTING. Anthony, 02/10/2026: *"when I said make it
 * configurable - this must be done from UI as it will be needed for future
 * years."* An app setting meant an Azure CLI command, a function app restart and
 * a trip to IT every July — which is not configuration, it is a deployment.
 *
 * ⛔ ONE ROW PER YEAR, NOT ONE VALUE. A single editable value still has to be
 * changed ON THE DAY. Miss it and every purchase from 1 July quietly charges
 * last year's project — the exact failure this phase exists to prevent, and the
 * kind nobody reports because the cost line looks deliberate. Set next year's
 * whenever; the rollover happens by itself.
 *
 * ⚠ THE CURRENT YEAR COMES FROM THE SERVER, in Sydney local time. Deriving it
 * from the browser clock would show the old year to a traveller between midnight
 * and 10am Sydney on 1 July, while a purchase charged the new one.
 */
export function StandingProjectPanel({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const config = useQuery({
    queryKey: ['hardware-cost-projects'],
    queryFn: ({ signal }) => getHardwareCostProjects(signal),
    retry: false,
  });

  const save = useMutation({
    mutationFn: ({ year, reference, name }: { year: number; reference: string; name: string | null }) =>
      setHardwareCostProject(year, { ProjectReference: reference, ProjectName: name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hardware-cost-projects'] });
      setEditing(null);
      setError(null);
    },
    onError: (e: unknown) => setError(
      e instanceof ApiError ? e.message : 'Could not save the standing project.'),
  });

  const currentYear = config.data?.CurrentFinancialYear ?? null;
  const years = config.data?.Years ?? [];
  const current = currentYear === null
    ? undefined
    : years.find((y) => y.FinancialYear === currentYear);

  return (
    <section className="mb-4 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
      <header className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-heading text-base font-bold text-navy">Purchasing</h2>
          <p className="mt-1 text-sm text-darkGrey/80">
            Which project a hardware purchase is charged to when whoever allocates it does not name
            one. Set a year ahead and it takes over by itself on 1 July.
          </p>
        </div>
        {isAdmin && editing === null && currentYear !== null && (
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white"
            onClick={() => { setEditing(currentYear); setError(null); }}
          >
            <Plus size={16} aria-hidden /> Set a year
          </button>
        )}
      </header>

      {config.isLoading && <p className="text-sm text-darkGrey/70">Loading…</p>}

      {/* ⛔ SAID LOUDLY. Until it is set, a purchase that does not name a project
          is refused. Not a crash — the allocation screen can always name one —
          but it is the next thing somebody should do. */}
      {currentYear !== null && !current && (
        <p role="alert" className="rounded border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-darkGrey">
          <AlertTriangle className="mr-1 inline h-4 w-4 text-orange" aria-hidden />
          <strong>No standing project is set for FY{currentYear}.</strong> Until one is, a purchase
          has to name its project on the allocation screen or it will be refused.
        </p>
      )}

      {current && (
        <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-darkGrey">
          <strong>FY{currentYear}</strong> — purchases are charged to{' '}
          <strong>{current.ProjectReference}</strong>
          {current.ProjectName ? ` · ${current.ProjectName}` : ''}.
        </p>
      )}

      {years.length > 0 && (
        <table aria-label="Standing project by financial year" className="mt-3 w-full text-left text-sm">
          <thead>
            <tr className="border-b border-lightGrey text-xs uppercase tracking-wide text-slate-600">
              <th className="py-2 pr-3">Financial year</th>
              <th className="py-2 pr-3">Project</th>
              <th className="py-2 pr-3">Set by</th>
              <th className="py-2"><span className="sr-only">Change</span></th>
            </tr>
          </thead>
          <tbody>
            {years.map((y) => (
              <tr key={y.FinancialYear} className="border-b border-lightGrey/60">
                <td className="py-2 pr-3 font-medium text-darkGrey">
                  FY{y.FinancialYear}
                  {y.FinancialYear === currentYear && (
                    <span className="ml-2 rounded bg-green/15 px-2 py-0.5 text-xs font-semibold text-green">
                      Current
                    </span>
                  )}
                  {/* ⚠ The whole reason this is per-year rather than one value:
                      a year set in advance is visibly waiting, so nobody has to
                      remember to do it on the morning of 1 July. */}
                  {currentYear !== null && y.FinancialYear > currentYear && (
                    <span className="ml-2 rounded bg-lightBlue/60 px-2 py-0.5 text-xs font-medium text-navy">
                      Ready for 1 July
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3 text-slate-600">
                  {y.ProjectReference}{y.ProjectName ? ` · ${y.ProjectName}` : ''}
                </td>
                <td className="py-2 pr-3 text-slate-600">{y.ModifiedBy ?? '—'}</td>
                <td className="py-2 text-right">
                  {isAdmin && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-navy"
                      aria-label={`Change FY${y.FinancialYear}`}
                      onClick={() => { setEditing(y.FinancialYear); setError(null); }}
                    >
                      <Pencil size={14} aria-hidden /> Change
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing !== null && currentYear !== null && (
        <StandingProjectEditor
          startYear={editing}
          currentYear={currentYear}
          existing={years}
          saving={save.isPending}
          onCancel={() => { setEditing(null); setError(null); }}
          onSave={(year, reference, name) => save.mutate({ year, reference, name })}
        />
      )}

      {error && <p role="alert" className="mt-2 text-sm text-red">{error}</p>}
    </section>
  );
}

/**
 * ⚠ THE PROJECT IS PICKED, NEVER TYPED. A reference typed from memory is how
 * `ITHW-FY26` — a project that does not exist in the Project Tracker at all —
 * came to be configured, and why the first live purchase failed on 01/10/2026.
 */
function StandingProjectEditor({
  startYear, currentYear, existing, saving, onCancel, onSave,
}: {
  startYear: number;
  currentYear: number;
  existing: HardwareCostProject[];
  saving: boolean;
  onCancel: () => void;
  onSave: (year: number, reference: string, name: string | null) => void;
}) {
  const [year, setYear] = useState(startYear);
  const [reference, setReference] = useState(
    existing.find((e) => e.FinancialYear === startYear)?.ProjectReference ?? '');
  const [search, setSearch] = useState('');

  const projects = useQuery({
    queryKey: ['project-options', search],
    queryFn: ({ signal }) => getProjectOptions(search || undefined, signal),
    retry: false,
  });

  // ⚠ This year and the next three. Nobody is setting FY2031's hardware project
  // today, and an open-ended number box invites a year typed as 27 — which the
  // database refuses, but only after somebody has wondered why.
  const years = [currentYear, currentYear + 1, currentYear + 2, currentYear + 3];
  const options = projects.data ?? [];
  const chosen = options.find((p) => p.ProjectReference === reference);
  const replacing = existing.find((e) => e.FinancialYear === year);

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Financial year
          </span>
          <select className="input" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                FY{y} — 1 July {y - 1} to 30 June {y}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Find a project
          </span>
          <input
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Reference or name"
          />
        </label>
      </div>

      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
          Project
        </span>
        <select className="input" value={reference} onChange={(e) => setReference(e.target.value)}>
          <option value="">Select…</option>
          {/* ⚠ The saved reference may not be in the current 50-row page, so it
              is offered explicitly. Without this, opening Change on a year and
              pressing Save would silently blank it. */}
          {reference !== '' && !chosen && (
            <option value={reference}>{reference} (currently set)</option>
          )}
          {options.map((p) => (
            <option key={p.ProjectReference} value={p.ProjectReference}>
              {p.ProjectReference} · {p.ProjectName}{p.Status ? ` · ${p.Status}` : ''}
            </option>
          ))}
        </select>
      </label>

      {/* ⚠ Not drawn as a failure. No list means migration 084 has not been
          applied, and the cause is the view rather than this screen. */}
      {projects.data && options.length === 0 && (
        <p className="mt-1 text-xs text-darkGrey/70">
          {search ? 'No live project matches that.' : 'No projects could be loaded.'}
        </p>
      )}

      {replacing && replacing.ProjectReference !== reference && (
        <p className="mt-2 rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
          ⚠ FY{year} is already set to <strong>{replacing.ProjectReference}</strong>. Saving
          replaces it. Purchases already made keep the project they were charged to.
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-darkGrey"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={reference === '' || saving}
          onClick={() => onSave(year, reference, chosen?.ProjectName ?? null)}
          className="rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
